import { GTM_ID, GTM_ID_STAGING } from "@/utils/constants";
import logger from "@/utils/logger";

// Determine which GTM container is active based on hostname
const getActiveGtmId = () => {
  if (typeof window === "undefined") return GTM_ID; // default to prod on SSR
  const hostname = window.location.hostname;
  if (hostname === "lexi.tips") return GTM_ID;
  // Treat all non-prod (including localhost) as staging container
  return GTM_ID_STAGING || GTM_ID;
};

const maskSensitiveData = (path) => {
  // Mask any user-specific IDs or sensitive data in URLs
  return path
    .replace(/\/user\/\d+/, "/user/[id]")
    .replace(/\/tip\/\d+/, "/tip/[id]");
};

// Generic event tracking function
export const trackEvent = (eventName, eventData = {}) => {
  try {
    const activeGtmId = getActiveGtmId();

    if (typeof window.dataLayer !== "undefined") {
      logger.log("GTM event (dataLayer.push)", {
        event: eventName,
        eventData,
        gtmId: activeGtmId,
      });
      window.dataLayer.push({
        event: eventName,
        ...eventData,
        timestamp: new Date().toISOString(),
      });
      return true;
    } else if (typeof window.gtag === "function") {
      logger.log("gtag event", {
        event: eventName,
        eventData,
        gtmId: activeGtmId,
      });
      window.gtag("event", eventName, eventData);
      return true;
    } else {
      logger.warn("Analytics not initialized, event not tracked", {
        eventName,
        eventData,
        gtmId: activeGtmId,
      });
      return false;
    }
  } catch (error) {
    logger.error("Error tracking event", { eventName, eventData, error });
    return false;
  }
};

// Button click tracking
export const trackButtonClick = (buttonName, additionalData = {}) => {
  return trackEvent("button_click", {
    button_name: buttonName,
    ...additionalData,
  });
};

// Race filter button click tracking
export const trackRaceFilterClick = (
  trackName,
  raceNumber,
  isNextToJump = false,
  isInProgress = false,
) => {
  return trackButtonClick("race_filter", {
    track_name: trackName,
    race_number: raceNumber,
    is_next_to_jump: isNextToJump,
    is_in_progress: isInProgress,
  });
};

// URL navigation tracking
export const trackNavigation = (url, source = "") => {
  const urlObj = new URL(url, window.location.origin);
  const params = Object.fromEntries(urlObj.searchParams);

  return trackEvent("navigation", {
    url: maskSensitiveData(urlObj.pathname + urlObj.search),
    path: maskSensitiveData(urlObj.pathname),
    source: source,
    params: params,
  });
};

// Racing page navigation tracking
export const trackRaceNavigation = (trackName, raceNumber, source = "") => {
  return trackEvent("race_navigation", {
    track_name: trackName,
    race_number: raceNumber,
    source: source,
  });
};

// Tip history toggle tracking
export const trackTipHistoryToggle = (isExpanded) => {
  return trackButtonClick("tip_history_toggle", {
    is_expanded: isExpanded,
  });
};

// Tip history click tracking with page context
export const trackTipHistoryClick = ({
  isExpanded,
  pageContext = "",
  path = "",
}) => {
  const currentPath =
    path ||
    (typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "");

  logger.log("tip_history_click event", {
    is_expanded: isExpanded,
    page_context: pageContext || currentPath,
    path: currentPath,
    gtmId: getActiveGtmId(),
  });

  return trackButtonClick("tip_history_click", {
    is_expanded: isExpanded,
    page_context: pageContext || currentPath,
    path: currentPath,
  });
};

// Favorite toggle tracking
export const trackFavoriteToggle = (horseId, horseName, action) => {
  return trackButtonClick("favorite_toggle", {
    horse_id: horseId,
    horse_name: horseName,
    action: action, // 'add' or 'remove'
  });
};

// Chat interactions tracking
export const trackChatInteraction = (action, additionalData = {}) => {
  return trackEvent("chat_interaction", {
    action: action, // 'submit', 'microphone', 'prev', 'next'
    ...additionalData,
  });
};

// Bottom navbar tracking
export const trackBottomNavClick = (tabName) => {
  return trackButtonClick("bottom_nav", {
    tab_name: tabName,
  });
};

// Tip display mode selection tracking
export const trackTipDisplayMode = ({
  tipsCount,
  isShowingAllTips,
  pagePath = "",
}) => {
  const currentPath =
    pagePath ||
    (typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "");

  logger.log("tip_display_mode_set event", {
    mode_tips_count: tipsCount,
    is_showing_all_tips: isShowingAllTips,
    mode_label: isShowingAllTips ? "all_tips" : "top_tips",
    page_path: currentPath,
    gtmId: getActiveGtmId(),
  });

  return trackEvent("tip_display_mode_set", {
    mode_tips_count: tipsCount,
    is_showing_all_tips: isShowingAllTips,
    mode_label: isShowingAllTips ? "all_tips" : "top_tips",
    page_path: currentPath,
  });
};

export const trackPageView = (path) => {
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: maskSensitiveData(path),
      user_type: "authenticated", // Track user segment, not individual
    });
  } else if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "page_view",
      page_path: maskSensitiveData(path),
    });
  }
};

// Initialize Google Analytics
export const initializeGA = () => {
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GTM_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    dataLayer.push(arguments);
  }
  gtag("js", new Date());
  gtag("config", GTM_ID);
};
